<template>
  <div>
    <!-- Page Title Area -->
    <div class="page-title-area">
      <div class="container">
        <h2>{{ title }}</h2>
      </div>
    </div>

    <!-- Page Content Area -->
    <section class="ptb-80">
      <div class="container">
        <div v-if="loading" class="text-center">
          <div class="spinner-border" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
        <div v-else-if="error" class="alert alert-danger" role="alert">
          {{ error }}
        </div>
        <div v-else class="page-content" v-html="content"></div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { fetchCmsContent } from '../utils/api'

export default {
  name: 'PageView',
  props: {
    slug: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const route = useRoute()
    const title = ref('')
    const content = ref('')
    const loading = ref(true)
    const error = ref(null)

    const fetchPage = async (slug) => {
      loading.value = true
      error.value = null
      
      try {
        console.log(`Fetching page with slug: ${slug}`)
        const response = await fetchCmsContent('page', slug)
        
        if (!response) {
          error.value = 'Page not found'
          return
        }
        
        if (response.Title) {
          title.value = response.Title
          content.value = response.Content || '' // Content now maps to Contents
          
          // Set meta information if available
          if (response.MetaTitle) {
            document.title = response.MetaTitle
          } else {
            document.title = `${response.Title} - AtOnline.com`
          }
          
          // Set meta description if available
          const metaDescription = document.querySelector('meta[name="description"]')
          if (metaDescription && response.MetaDescription) {
            metaDescription.setAttribute('content', response.MetaDescription)
          }
        } else {
          console.warn('Page response missing Title:', response)
          error.value = 'Page content is invalid or incomplete'
        }
      } catch (err) {
        console.error('Error fetching page:', err)
        // Provide more specific error message based on error type
        if (err.message && err.message.includes('not found')) {
          error.value = 'Page not found'
        } else if (err.response && err.response.status === 404) {
          error.value = 'Page not found'
        } else if (err.response && err.response.status >= 500) {
          error.value = 'Server error when loading page. Please try again later.'
        } else {
          error.value = 'Error loading page content'
        }
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchPage(props.slug)
    })

    // Watch for route changes
    watch(() => route.params.slug, (newSlug) => {
      if (newSlug && newSlug !== props.slug) {
        fetchPage(newSlug)
      }
    })

    return {
      title,
      content,
      loading,
      error
    }
  }
}
</script>

<style scoped>
.page-content {
  min-height: 300px;
}
</style>